import { useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { openEnquiry, inr } from "../../lib/api";
import { reducingEMI, flatEMI, equivReducingFromFlat, equivFlatFromReducing, affordablePrincipal, round, floorTo } from "../../lib/finance";
import { Reveal } from "../Reveal";

const field = "w-full min-h-[44px] rounded-2xl border-2 border-[#e1e2e5] px-4 py-2.5 font-medium focus:border-[#004040] outline-none bg-white";
const label = "block text-sm font-bold mb-1.5 mt-3";
const getBtn = "min-h-[44px] px-5 rounded-full bg-[#f06400] text-[#191c1f] font-extrabold hover:bg-[#d85800]";

function Stat({ k, v }) {
  return (
    <div className="rounded-2xl bg-[#f4f5f6] px-4 py-3 min-w-0">
      <div className="text-xs font-bold text-[#6b6f76]">{k}</div>
      <div className="text-base sm:text-lg font-extrabold tracking-[-0.02em] break-words">{v}</div>
    </div>
  );
}

function EmiCalc() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(60);
  const [basis, setBasis] = useState("reducing");
  const r = useMemo(() => {
    const emi = basis === "reducing" ? reducingEMI(amount, rate, tenure) : flatEMI(amount, rate, tenure);
    const total = emi * tenure;
    const interest = total - amount;
    const equiv = basis === "reducing" ? equivFlatFromReducing(amount, rate, tenure) : equivReducingFromFlat(amount, rate, tenure);
    return { emi: round(emi), total: round(total), interest: round(interest), equiv };
  }, [amount, rate, tenure, basis]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="min-w-0">
        <label className={label}>Loan amount: Rs {inr(amount)}</label>
        <input type="range" min="50000" max="5000000" step="10000" value={amount} className="w-full accent-[#f06400]"
          onChange={(e) => setAmount(+e.target.value)} data-testid="emi-amount" />
        <label className={label}>Interest rate: {rate}% per year</label>
        <input type="range" min="6" max="30" step="0.1" value={rate} className="w-full accent-[#f06400]"
          onChange={(e) => setRate(+e.target.value)} data-testid="emi-rate" />
        <label className={label}>Tenure: {tenure} months</label>
        <input type="range" min="6" max="240" step="1" value={tenure} className="w-full accent-[#f06400]"
          onChange={(e) => setTenure(+e.target.value)} data-testid="emi-tenure" />
        <div className="mt-3 inline-flex rounded-full bg-[#f4f5f6] p-1" role="tablist" aria-label="Rate basis">
          {["reducing", "flat"].map((b) => (
            <button key={b} data-testid={`emi-basis-${b}`} onClick={() => setBasis(b)}
              className={`px-4 py-2 rounded-full text-sm font-bold ${basis === b ? "bg-[#191c1f] text-white" : ""}`}>
              {b === "reducing" ? "Reducing balance" : "Flat"}
            </button>
          ))}
        </div>
      </div>
      <div aria-live="polite" data-testid="emi-result">
        <div className="rounded-[28px] bg-[#191c1f] text-white p-6">
          <div className="text-sm text-white/70 font-bold">Monthly EMI</div>
          <div className="text-4xl font-extrabold tracking-[-0.03em]">Rs {inr(r.emi)}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Stat k="Principal" v={`Rs ${inr(amount)}`} />
          <Stat k="Total interest" v={`Rs ${inr(r.interest)}`} />
          <Stat k="Total payable" v={`Rs ${inr(r.total)}`} />
          <Stat k={basis === "reducing" ? "Same as flat" : "Same as reducing"} v={`${r.equiv.toFixed(2)}%`} />
        </div>
        <button className={`${getBtn} mt-4`} data-testid="emi-get-options"
          onClick={() => openEnquiry({ amount_requested: amount, source_tool: "emi_calculator" })}>
          Get options for this
        </button>
      </div>
    </div>
  );
}

function Afford() {
  const [income, setIncome] = useState(60000);
  const [emis, setEmis] = useState(5000);
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(60);
  const [share, setShare] = useState(50);
  const res = useMemo(() => {
    const comfortable = Math.max(0, (income * share) / 100 - emis);
    const principal = floorTo(affordablePrincipal(comfortable, rate, tenure), 10000);
    return { comfortable: round(comfortable), principal };
  }, [income, emis, rate, tenure, share]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className={label} htmlFor="af-income">Monthly take-home income (Rs)</label>
        <input id="af-income" className={field} type="number" value={income} onChange={(e) => setIncome(+e.target.value)} data-testid="afford-income" />
        <label className={label} htmlFor="af-emis">Existing monthly EMIs (Rs)</label>
        <input id="af-emis" className={field} type="number" value={emis} onChange={(e) => setEmis(+e.target.value)} data-testid="afford-emis" />
        <div className="grid grid-cols-2 gap-2">
          <div><label className={label} htmlFor="af-rate">Assumed rate %</label>
            <input id="af-rate" className={field} type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} /></div>
          <div><label className={label} htmlFor="af-tenure">Tenure (months)</label>
            <input id="af-tenure" className={field} type="number" value={tenure} onChange={(e) => setTenure(+e.target.value)} /></div>
        </div>
        <label className={label}>EMI share of income</label>
        <div className="inline-flex rounded-full bg-[#f4f5f6] p-1">
          {[40, 50].map((s) => (
            <button key={s} onClick={() => setShare(s)} data-testid={`afford-share-${s}`}
              className={`px-4 py-2 rounded-full text-sm font-bold ${share === s ? "bg-[#191c1f] text-white" : ""}`}>{s}%</button>
          ))}
        </div>
      </div>
      <div aria-live="polite" data-testid="afford-result">
        <div className="rounded-[28px] bg-[#004040] text-white p-6">
          <div className="text-sm text-white/70 font-bold">Comfortable new EMI</div>
          <div className="text-3xl font-extrabold">Rs {inr(res.comfortable)}</div>
          <div className="mt-4 text-sm text-white/70 font-bold">Indicative loan amount</div>
          <div className="text-3xl font-extrabold">Rs {inr(res.principal)}</div>
        </div>
        <p className="mt-3 text-sm text-[#6b6f76]">A rule of thumb, not an offer. The lender decides the final amount.</p>
        <button className={`${getBtn} mt-3`} data-testid="afford-get-options"
          onClick={() => openEnquiry({ amount_requested: res.principal, source_tool: "affordability" })}>
          Get options for this
        </button>
      </div>
    </div>
  );
}

const DOC_BASE = ["PAN card", "Aadhaar or another government photo ID", "Address proof", "Recent photographs", "Bank account details with an active mobile number"];
const DOC_SAL = ["Last 3 months salary slips", "Last 6 months bank statement", "Form 16 or latest ITR, if available"];
const DOC_SELF = ["Last 2 years ITR with computation", "Last 6 to 12 months business bank statements", "GST returns, if registered", "Business proof such as registration, Udyam or a shop licence"];

function DocsChecklist({ settings }) {
  const products = settings?.products || {};
  const loanProducts = Object.entries(products).filter(([k]) => !k.startsWith("tax_") && k !== "partner_signup");
  const [loan, setLoan] = useState("personal_loan");
  const [emp, setEmp] = useState("salaried");
  const list = [...DOC_BASE, ...(emp === "salaried" ? DOC_SAL : DOC_SELF)];
  const copy = () => navigator.clipboard?.writeText(`Documents for ${products[loan]} (${emp}):\n` + list.map((x) => "- " + x).join("\n"));
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className={label} htmlFor="dc-loan">Loan type</label>
        <select id="dc-loan" className={field} value={loan} onChange={(e) => setLoan(e.target.value)} data-testid="docs-loan">
          {loanProducts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className={label}>Employment</label>
        <div className="inline-flex rounded-full bg-[#f4f5f6] p-1">
          {[["salaried", "Salaried"], ["self", "Self-employed"]].map(([v, l]) => (
            <button key={v} onClick={() => setEmp(v)} data-testid={`docs-emp-${v}`}
              className={`px-4 py-2 rounded-full text-sm font-bold ${emp === v ? "bg-[#191c1f] text-white" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      <div data-testid="docs-result">
        <ul className="rounded-[28px] border border-[#e1e2e5] p-5 space-y-2">
          {list.map((d) => <li key={d} className="flex gap-2 text-sm"><span className="text-[#004040] font-extrabold">✓</span>{d}</li>)}
        </ul>
        <div className="flex gap-2 mt-3">
          <button className="min-h-[44px] px-5 rounded-full border-2 border-[#191c1f] font-bold" data-testid="docs-copy" onClick={copy}>Copy list</button>
          <button className={getBtn} data-testid="docs-get-options" onClick={() => openEnquiry({ product: loan, source_tool: "documents" })}>Get options for this</button>
        </div>
      </div>
    </div>
  );
}

function Converter() {
  const [mode, setMode] = useState("flat");
  const [rate, setRate] = useState(8);
  const [tenure, setTenure] = useState(36);
  const [amount, setAmount] = useState(500000);
  const r = useMemo(() => {
    if (mode === "flat") {
      const emi = flatEMI(amount, rate, tenure);
      const total = emi * tenure, interest = total - amount;
      return { emi: round(emi), interest: round(interest), total: round(total), other: equivReducingFromFlat(amount, rate, tenure), otherLabel: "Equivalent reducing rate" };
    }
    const emi = reducingEMI(amount, rate, tenure);
    const total = emi * tenure, interest = total - amount;
    return { emi: round(emi), interest: round(interest), total: round(total), other: equivFlatFromReducing(amount, rate, tenure), otherLabel: "Equivalent flat rate" };
  }, [mode, rate, tenure, amount]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className={label}>I have a…</label>
        <div className="inline-flex rounded-full bg-[#f4f5f6] p-1">
          {[["flat", "Flat rate"], ["reducing", "Reducing rate"]].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)} data-testid={`conv-mode-${v}`}
              className={`px-4 py-2 rounded-full text-sm font-bold ${mode === v ? "bg-[#191c1f] text-white" : ""}`}>{l}</button>
          ))}
        </div>
        <label className={label} htmlFor="cv-rate">Rate %</label>
        <input id="cv-rate" className={field} type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} data-testid="conv-rate" />
        <label className={label} htmlFor="cv-tenure">Tenure (months)</label>
        <input id="cv-tenure" className={field} type="number" value={tenure} onChange={(e) => setTenure(+e.target.value)} data-testid="conv-tenure" />
        <label className={label} htmlFor="cv-amount">Amount (Rs)</label>
        <input id="cv-amount" className={field} type="number" value={amount} onChange={(e) => setAmount(+e.target.value)} data-testid="conv-amount" />
      </div>
      <div aria-live="polite" data-testid="conv-result">
        <div className="rounded-[28px] bg-[#191c1f] text-white p-6">
          <div className="text-sm text-white/70 font-bold">{r.otherLabel}</div>
          <div className="text-4xl font-extrabold">{r.other.toFixed(2)}%</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <Stat k="EMI" v={`Rs ${inr(r.emi)}`} />
          <Stat k="Interest" v={`Rs ${inr(r.interest)}`} />
          <Stat k="Total" v={`Rs ${inr(r.total)}`} />
        </div>
        <p className="mt-3 text-sm text-[#6b6f76]">Same loan, same cost — just quoted two ways.</p>
      </div>
    </div>
  );
}

export function Tools({ settings }) {
  return (
    <section id="tools" className="bg-[#f4f5f6] py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">Free tools</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Plan before you borrow.</h2>
        </Reveal>
        <Reveal delay={100} className="mt-8">
          <Tabs defaultValue="emi">
            <TabsList className="flex flex-wrap h-auto bg-white rounded-3xl sm:rounded-full p-1 gap-1 justify-start">
              <TabsTrigger value="emi" data-testid="tool-tab-emi-calculator" className="rounded-full data-[state=active]:bg-[#191c1f] data-[state=active]:text-white font-bold px-4 py-2">EMI calculator</TabsTrigger>
              <TabsTrigger value="afford" data-testid="tool-tab-affordability" className="rounded-full data-[state=active]:bg-[#191c1f] data-[state=active]:text-white font-bold px-4 py-2">How much can I afford</TabsTrigger>
              <TabsTrigger value="docs" data-testid="tool-tab-documents" className="rounded-full data-[state=active]:bg-[#191c1f] data-[state=active]:text-white font-bold px-4 py-2">Documents checklist</TabsTrigger>
              <TabsTrigger value="conv" data-testid="tool-tab-converter" className="rounded-full data-[state=active]:bg-[#191c1f] data-[state=active]:text-white font-bold px-4 py-2">Flat vs reducing</TabsTrigger>
            </TabsList>
            <div className="bg-white rounded-[28px] p-4 sm:p-6 md:p-8 mt-4">
              <TabsContent value="emi"><EmiCalc /></TabsContent>
              <TabsContent value="afford"><Afford /></TabsContent>
              <TabsContent value="docs"><DocsChecklist settings={settings} /></TabsContent>
              <TabsContent value="conv"><Converter /></TabsContent>
            </div>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
}
